import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Heart, Moon } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './OvulationCalculatorPage/components/CalculationForm';
import ResultsOverview from './OvulationCalculatorPage/components/ResultsOverview';
import FertilityCalendar from './OvulationCalculatorPage/components/FertilityCalendar';
import QuickAdjustmentSidebar from './OvulationCalculatorPage/components/QuickAdjustmentSidebar';
import CycleHistorySidebar from './OvulationCalculatorPage/components/CycleHistorySidebar';
import TipsSidebar from './OvulationCalculatorPage/components/TipsSidebar';
import InfoContent from './OvulationCalculatorPage/components/InfoContent';

// Импорт утилит для работы с датами
import { addDays, calculateDateDifference, formatDate } from '../../../utils/calculations/date';

// Определение начального состояния формы
export const initialFormData = {
  lastPeriodDate: new Date().toISOString().split('T')[0], // Сегодня по умолчанию
  cycleLength: 28, // Средняя продолжительность цикла в днях
  periodLength: 5, // Средняя продолжительность менструации в днях
  lutealPhaseLength: 14, // Средняя длительность лютеиновой фазы
  includeSymptoms: false, // Учитывать симптомы
  includeBbt: false, // Учитывать базальную температуру
  bbtRise: 0.4, // Повышение базальной температуры в градусах C
  includeCervicalMucus: false, // Учитывать цервикальную слизь
  ovulationTestDate: '', // Дата положительного овуляционного теста
  plannedPregnancy: true, // Планирование беременности (true) или контрацепция (false)
};

// Определение типов циклов
export const cycleTypes = [
  { id: 'regular', name: 'Регулярный', description: 'Цикл регулярный, без значительных отклонений' },
  { id: 'irregular', name: 'Нерегулярный', description: 'Цикл с отклонениями в длительности' },
  { id: 'pcos', name: 'СПКЯ', description: 'Синдром поликистозных яичников' },
  { id: 'perimenopause', name: 'Перименопауза', description: 'Переходный период к менопаузе' },
  { id: 'postpartum', name: 'Послеродовой', description: 'Восстановление цикла после родов' },
  { id: 'post-pill', name: 'После отмены ОК', description: 'Восстановление после оральных контрацептивов' }
];

// Определение симптомов овуляции
export const ovulationSymptoms = [
  { id: 'cervicalMucus', name: 'Цервикальная слизь', description: 'Выделения становятся более прозрачными и эластичными' },
  { id: 'bbtRise', name: 'Повышение базальной температуры', description: 'Повышение базальной температуры на 0.2-0.5°C' },
  { id: 'ovulationPain', name: 'Боль при овуляции', description: 'Односторонние боли внизу живота (миттельшмерц)' },
  { id: 'libidoIncrease', name: 'Повышение либидо', description: 'Естественное увеличение полового влечения' },
  { id: 'breastTenderness', name: 'Болезненность груди', description: 'Чувствительность и дискомфорт в груди' },
  { id: 'spotting', name: 'Мажущие выделения', description: 'Незначительные кровянистые выделения' },
  { id: 'moodChanges', name: 'Перепады настроения', description: 'Эмоциональная нестабильность из-за гормональных изменений' }
];

/**
 * OvulationCalculatorPage Component - Полный калькулятор овуляции с отслеживанием фертильных дней,
 * прогнозированием даты следующей менструации и планированием беременности.
 */
const OvulationCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('ovulation-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор овуляции | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте дни овуляции, фертильное окно и безопасные дни для планирования беременности или контрацепции. Точный календарь овуляции и менструального цикла с учетом индивидуальных особенностей.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCycles, setSavedCycles] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных циклов из localStorage ---
  useEffect(() => {
    const savedCyclesData = localStorage.getItem('savedCycles');
    if (savedCyclesData) {
      try {
        setSavedCycles(JSON.parse(savedCyclesData));
      } catch (e) {
        console.error("Error loading saved cycles:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'pregnancy-planning':
          newState.plannedPregnancy = true;
          break;
        case 'contraception':
          newState.plannedPregnancy = false;
          break;
        case 'irregular-cycles':
          newState.cycleLength = 35;
          break;
        case 'post-pill':
          newState.cycleLength = 32;
          break;
        case 'pcos':
          newState.cycleLength = 40;
          break;
        case 'advanced':
          newState.includeSymptoms = true;
          newState.includeBbt = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setCalendarData([]);
    setErrors({});

  }, [subToolId]);

  // Функция расчета фертильных и безопасных дней
  const calculateFertilityWindow = (date, cycleLength, lutealPhaseLength) => {
    // Преобразование даты последней менструации в объект Date
    const lastPeriodDate = new Date(date);

    // Расчет предполагаемого дня овуляции (цикл минус лютеиновая фаза)
    const ovulationDay = cycleLength - lutealPhaseLength;

    // Дата овуляции
    const ovulationDate = new Date(lastPeriodDate);
    ovulationDate.setDate(lastPeriodDate.getDate() + ovulationDay - 1);

    // Фертильное окно (5 дней до овуляции + день овуляции + 1 день после)
    const fertileStartDate = new Date(ovulationDate);
    fertileStartDate.setDate(ovulationDate.getDate() - 5);

    const fertileEndDate = new Date(ovulationDate);
    fertileEndDate.setDate(ovulationDate.getDate() + 1);

    // Наиболее фертильные дни (день до овуляции и день овуляции)
    const peakFertileStartDate = new Date(ovulationDate);
    peakFertileStartDate.setDate(ovulationDate.getDate() - 1);
    
    const peakFertileEndDate = new Date(ovulationDate);

    // Дата предполагаемой следующей менструации
    const nextPeriodDate = new Date(lastPeriodDate);
    nextPeriodDate.setDate(lastPeriodDate.getDate() + cycleLength);

    // Дата окончания текущей менструации
    const periodEndDate = new Date(lastPeriodDate);
    periodEndDate.setDate(lastPeriodDate.getDate() + Number(formData.periodLength) - 1);

    return {
      ovulationDate,
      fertileStartDate,
      fertileEndDate,
      peakFertileStartDate,
      peakFertileEndDate,
      nextPeriodDate,
      periodEndDate
    };
  };

  // Сохранение цикла в localStorage
  const saveCycle = (notes = '') => {
    if (!results) return;

    const newSavedCycle = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      notes: notes || `Цикл ${formatDate(formData.lastPeriodDate, 'short')}`
    };

    const updatedSavedCycles = [...savedCycles, newSavedCycle];
    setSavedCycles(updatedSavedCycles);
    localStorage.setItem('savedCycles', JSON.stringify(updatedSavedCycles));

    return newSavedCycle;
  };

  // Удаление сохраненного цикла
  const deleteSavedCycle = (id) => {
    const updatedSavedCycles = savedCycles.filter(cycle => cycle.id !== id);
    setSavedCycles(updatedSavedCycles);
    localStorage.setItem('savedCycles', JSON.stringify(updatedSavedCycles));
  };

  // Загрузка сохраненного цикла
  const loadSavedCycle = (id) => {
    const cycleToLoad = savedCycles.find(cycle => cycle.id === id);
    if (cycleToLoad) {
      setFormData(cycleToLoad.formData);
      setResults(cycleToLoad.results);
      // Пересчитываем данные календаря
      if (cycleToLoad.results && cycleToLoad.results.ovulationDate) {
        const dates = calculateFertilityWindow(
          cycleToLoad.formData.lastPeriodDate,
          cycleToLoad.formData.cycleLength,
          cycleToLoad.formData.lutealPhaseLength
        );
        generateCalendarData(dates);
      }
    }
  };

  // Генерация данных для календаря
  const generateCalendarData = (dates) => {
    // Создаем календарь на 3 месяца вперед
    const startDate = new Date(formData.lastPeriodDate);
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + 3);

    const calendar = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      let dateType = '';
      let tooltip = '';

      // Определение типа дня
      if (isSameDay(currentDate, dates.ovulationDate)) {
        dateType = 'ovulation';
        tooltip = 'День овуляции';
      } else if (
        currentDate >= dates.fertileStartDate && 
        currentDate <= dates.fertileEndDate
      ) {
        if (currentDate >= dates.peakFertileStartDate && 
            currentDate <= dates.peakFertileEndDate) {
          dateType = 'peak-fertile';
          tooltip = 'Пик фертильности';
        } else {
          dateType = 'fertile';
          tooltip = 'Фертильный день';
        }
      } else if (
        currentDate >= startDate && 
        currentDate <= dates.periodEndDate
      ) {
        dateType = 'period';
        tooltip = 'Менструация';
      } else if (
        (currentDate > dates.periodEndDate && currentDate < dates.fertileStartDate) ||
        (currentDate > dates.fertileEndDate)
      ) {
        dateType = 'safe';
        tooltip = 'Относительно безопасный день';
      }

      calendar.push({
        date: new Date(currentDate),
        dateStr,
        dateType,
        tooltip
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    setCalendarData(calendar);
  };

  // Вспомогательная функция для сравнения дат
  const isSameDay = (date1, date2) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Heart className="w-8 h-8 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор овуляции'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setCalendarData={setCalendarData}
              cycleTypes={cycleTypes}
              ovulationSymptoms={ovulationSymptoms}
              saveCycle={saveCycle}
              calculateFertilityWindow={calculateFertilityWindow}
              generateCalendarData={generateCalendarData}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <FertilityCalendar 
                  calendarData={calendarData} 
                  results={results}
                  formData={formData}
                />
              </>
            )}

            {/* --- Информационный контент об овуляции и планировании беременности --- */}
            <InfoContent formData={formData} />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              calculateFertilityWindow={calculateFertilityWindow}
              setResults={setResults}
              generateCalendarData={generateCalendarData}
            />

            {/* --- Панель истории циклов --- */}
            <CycleHistorySidebar 
              savedCycles={savedCycles}
              deleteSavedCycle={deleteSavedCycle}
              loadSavedCycle={loadSavedCycle}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar plannedPregnancy={formData.plannedPregnancy} />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-rose-500 focus:ring-rose-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-rose-600 focus:ring-rose-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-rose-600 focus:ring-rose-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Calendar Styling */
        .fertility-calendar .ovulation-day {
          @apply bg-rose-500 hover:bg-rose-600 text-white font-bold;
        }
        .fertility-calendar .fertile-day {
          @apply bg-rose-200 dark:bg-rose-800 hover:bg-rose-300 dark:hover:bg-rose-700;
        }
        .fertility-calendar .peak-fertile-day {
          @apply bg-rose-300 dark:bg-rose-700 hover:bg-rose-400 dark:hover:bg-rose-600 font-semibold;
        }
        .fertility-calendar .period-day {
          @apply bg-purple-200 dark:bg-purple-900 hover:bg-purple-300 dark:hover:bg-purple-800;
        }
        .fertility-calendar .safe-day {
          @apply bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50;
        }
      `}</style>
    </div>
  );
};

export default OvulationCalculatorPage;